import { type FunctionComponent, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useAuth } from 'react-oidc-context';

import { config } from '../../../../config/config';
import { getClient } from '../../../main/graphql';
import { ContentMarkdown } from '../../../shared/components/content-markdown/content-markdown';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { TitledSection } from '../../../shared/components/titled-section/titled-section';
import { useToast } from '../../../shared/hooks/use-toast';
import { useUserCurrencies } from '../../../shared/hooks/use-user-currencies';
import { createStyleHelper } from '../../../shared/utils/class-names';
import { getErrorMessage } from '../../../shared/utils/convert-error';
import { exists } from '../../../shared/utils/filters';
import { BudgetLimitForm } from '../../components/budget-limit-form/budget-limit-form';
import { BudgetLimitHistoryTable } from '../../components/buget-limit-history-table/budget-limit-history-table';
import { DepositLimitForm } from '../../components/deposit-limit-form/deposit-limit-form';
import type { BudgetLimitsTransactionTypes } from '../../limits.interface';
import { UserBudgetLimitMediatorTypeView } from '../../limits.interface';
import type {
    IDeleteUserBudgetLimitMutation,
    IDeleteUserBudgetLimitMutationVariables,
} from '../../queries/budget-limit.graphql';
import {
    DeleteUserBudgetLimitDocument,
    ICreatorName,
    useGetUserBudgetLimitsQuery,
    useLimitsUpdateSubscription,
} from '../../queries/budget-limit.graphql';
import type {
    IDeleteUserMaxWagerLimitMutation,
    IDeleteUserMaxWagerLimitMutationVariables,
} from '../../queries/max-wager-limit.graphql';
import {
    useGetUserMaxWagerLimitsQuery,
    DeleteUserMaxWagerLimitDocument,
    useMaxWagerLimitsUpdateSubscription,
} from '../../queries/max-wager-limit.graphql';

import styles from './budget-limits.module.scss';

const style = createStyleHelper(styles, 'budget-limits');

export interface IBudgetLimitsProps {
    mediatorType: UserBudgetLimitMediatorTypeView;
}

export const BudgetLimits: FunctionComponent<IBudgetLimitsProps> = ({ mediatorType }) => {
    const intl = useIntl();
    const auth = useAuth();
    const toast = useToast();
    const userProfileId = auth.user?.profile?.user_profile_id as string;

    const [{ data: userLimitsData }, refetch] = useGetUserBudgetLimitsQuery({
        pause: !userProfileId,
        variables: {
            xTenantID: config.TENANT_ID,
            userProfileId: userProfileId as string,
        },
        requestPolicy: 'cache-and-network',
    });

    const [{ data: userMaxWagerData }, refetchMaxWage] = useGetUserMaxWagerLimitsQuery({
        pause: !userProfileId,
        variables: {
            xTenantID: config.TENANT_ID,
            userProfileId: userProfileId as string,
        },
        requestPolicy: 'cache-and-network',
    });

    const [{ data }] = useLimitsUpdateSubscription({
        pause: !userProfileId,
        variables: {
            userProfileId: userProfileId as string,
        },
    });

    const [{ data: dataMaxWager }] = useMaxWagerLimitsUpdateSubscription({
        pause: !userProfileId,
        variables: {
            userProfileId: userProfileId as string,
        },
    });

    useEffect(() => {
        if (data?.limitsUpdated) {
            refetch();
        }
        if (dataMaxWager?.maxLimitLimitsUpdated) {
            refetchMaxWage();
        }
    }, [data, dataMaxWager, refetch, refetchMaxWage]);

    const onLimitCancel = useCallback(
        async (limitId: string, type?: BudgetLimitsTransactionTypes) => {
            const { error } = await getClient(false)
                .mutation<
                    IDeleteUserMaxWagerLimitMutation | IDeleteUserBudgetLimitMutation,
                    | IDeleteUserBudgetLimitMutationVariables
                    | IDeleteUserMaxWagerLimitMutationVariables
                >(!type ? DeleteUserMaxWagerLimitDocument : DeleteUserBudgetLimitDocument, {
                    userProfileId: userProfileId,
                    xTenantID: config.TENANT_ID,
                    creatorName: ICreatorName.User,
                    limitId: limitId,
                })
                .toPromise();

            if (error) {
                toast.error(getErrorMessage(intl, 'user.limits.cancelled', error));
                return;
            }

            toast.success(
                intl.formatMessage({
                    id: 'user.limit.cancelled.success',
                    defaultMessage: 'User Limit Cancelled',
                }),
            );
            refetch();
        },
        [userProfileId, toast, intl, refetch],
    );

    const userLimits = userLimitsData?.userBudgetLimits;
    const useMaxWagerLimits = userMaxWagerData?.userMaxWagerLimits;
    const { currencies } = useUserCurrencies();

    return (
        <div className={style()}>
            {mediatorType === UserBudgetLimitMediatorTypeView.DEPOSIT ? (
                <DepositLimitForm availableCurrencies={currencies} />
            ) : (
                <BudgetLimitForm availableCurrencies={currencies} mediatorType={mediatorType} />
            )}

            <TitledSection
                scrollable
                title={
                    <FormattedMessage
                        id="budget-limits.history.title"
                        defaultMessage="Budget Limits History"
                    />
                }
            >
                <BudgetLimitHistoryTable
                    history={userLimits?.filter(exists) ?? []}
                    onLimitCancel={onLimitCancel}
                />
            </TitledSection>

            <TitledSection
                scrollable
                title={
                    <FormattedMessage
                        id="max-wager-limits.history.title"
                        defaultMessage="Max Wager Limits History"
                    />
                }
            >
                <BudgetLimitHistoryTable
                    history={useMaxWagerLimits?.filter(exists) ?? []}
                    onLimitCancel={onLimitCancel}
                />
            </TitledSection>

            <section className={style('terms')}>
                <ContentMarkdown
                    markdown={intl.formatMessage({
                        id: 'budget-limits.terms-and-conditions',
                    })}
                />
            </section>
        </div>
    );
};
